// id.<root>/devices — TanStack Table over the adapter's MFA device list.
// Per-row buttons mirror the worker route: "Email activation link" if the
// device isn't active, "Remove" with a window.confirm guard. Mutations go
// through authClient.fetchWithCsrf so the double-submit __Host-mf_csrf token
// is attached automatically. (Folded into id-identity from id-online so the
// account hub's 2FA management is self-contained.)

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { authClient } from "../lib/auth";
import { brandLabel, resolveBrand } from "../lib/brand";
import type { SessionView } from "../lib/session";

interface MfaDevice {
  deviceId: string;
  sealoneId?: string;
  label: string;
  state: string;
  preferredForLogin: boolean;
  preferredForTransactions: boolean;
  recoveryDevice: boolean;
}

async function fetchDevices(userId: string): Promise<MfaDevice[]> {
  const res = await fetch(
    `/api/mf-auth-adapter/v1/mfa/devices/${encodeURIComponent(userId)}`,
    { credentials: "same-origin" },
  );
  if (!res.ok) throw new Error(`devices_load_failed_${res.status}`);
  return (await res.json()) as MfaDevice[];
}

async function deleteDevice(userId: string, deviceId: string): Promise<void> {
  const res = await authClient.fetchWithCsrf(
    `/api/mf-auth-adapter/v1/mfa/devices/${encodeURIComponent(userId)}/${encodeURIComponent(deviceId)}`,
    { method: "DELETE" },
  );
  if (!res.ok && res.status !== 204) throw new Error(`delete_failed_${res.status}`);
}

async function triggerActivation(userId: string, deviceId: string): Promise<void> {
  const res = await authClient.fetchWithCsrf(
    `/api/mf-auth-adapter/v1/mfa/devices/${encodeURIComponent(userId)}/${encodeURIComponent(deviceId)}/trigger-email-activation`,
    { method: "POST" },
  );
  if (!res.ok && res.status !== 204) throw new Error(`activate_failed_${res.status}`);
}

export interface DevicesProps {
  session: SessionView;
}

export function Devices({ session }: DevicesProps) {
  const queryClient = useQueryClient();
  const userId = session.sub;

  const devices = useQuery({
    queryKey: ["mfa", "devices", userId],
    queryFn: () => fetchDevices(userId),
    retry: false,
  });

  const removeMutation = useMutation({
    mutationFn: (deviceId: string) => deleteDevice(userId, deviceId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["mfa", "devices", userId] }),
  });

  const activateMutation = useMutation({
    mutationFn: (deviceId: string) => triggerActivation(userId, deviceId),
  });

  const columns: ColumnDef<MfaDevice>[] = [
    {
      header: "Label",
      accessorFn: (row) => row.label || "(unnamed device)",
      id: "label",
      cell: (ctx) => (
        <span>
          <strong>{ctx.getValue<string>()}</strong>
          {ctx.row.original.preferredForLogin ? (
            <span title="Preferred for login" style={{ marginLeft: 6 }}>★</span>
          ) : null}
        </span>
      ),
    },
    {
      header: "State",
      accessorFn: (row) => row.state,
      id: "state",
      cell: (ctx) => {
        const raw = ctx.getValue<string>();
        // Adapter passes DMS' lowercase state through; normalise for display.
        const lower = raw.toLowerCase();
        return (
          <span className={`devices-state devices-state-${lower}`}>
            {raw.toUpperCase()}
          </span>
        );
      },
    },
    {
      header: "Recovery",
      accessorFn: (row) => row.recoveryDevice,
      id: "recovery",
      cell: (ctx) => (ctx.getValue<boolean>() ? "yes" : ""),
    },
    {
      header: "Actions",
      id: "actions",
      cell: (ctx) => {
        const device = ctx.row.original;
        const isActive = device.state.toLowerCase() === "active";
        return (
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            {!isActive ? (
              <button
                type="button"
                className="hub-cta-button secondary"
                onClick={() => activateMutation.mutate(device.deviceId)}
                disabled={activateMutation.isPending}
              >
                Email activation link
              </button>
            ) : null}
            <button
              type="button"
              className="hub-cta-button secondary"
              onClick={() => {
                const label = device.label || "this device";
                if (
                  window.confirm(
                    `Remove ${label}? You won't be able to use it for 2FA after this.`,
                  )
                ) {
                  removeMutation.mutate(device.deviceId);
                }
              }}
              disabled={removeMutation.isPending}
            >
              Remove
            </button>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: devices.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <header className="hub-page-header">
        <h1>Two-factor devices</h1>
        <p>
          Manage devices used for sign-in and transaction approval on{" "}
          {brandLabel(resolveBrand())}.
        </p>
      </header>
      <div className="hub-banner hub-banner-warn" role="note">
        First-device enrolment requires the SealOne mobile pairing handshake
        — that flow is out of PoC scope. For stakeholder demos use{" "}
        <Link to="/dev/mfa">/dev/mfa</Link> (DEV/UAT only).
      </div>
      {devices.isLoading ? (
        <p className="hub-page-header">Loading devices…</p>
      ) : devices.isError ? (
        <p className="hub-page-header" style={{ color: "var(--hub-danger)" }}>
          Could not load devices: {String(devices.error)}
        </p>
      ) : (
        <table className="devices-table" data-testid="devices-table">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id}>
                    {h.isPlaceholder
                      ? null
                      : flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center" }}>
                  You don&apos;t have any 2FA devices yet.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
      <main className="hub-cta-row">
        <Link to="/" className="hub-cta-button secondary">
          Back to account
        </Link>
      </main>
    </>
  );
}
